import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form } from 'formik';
import { object, bool, number } from 'yup';
import { Box } from '@mui/material';

import {
  AlertStyled,
  DataSourceBox,
  DataSourceName,
  DataSourceType,
  DescriptionStyled,
  FormContainer,
  IngestionContainer,
  IngestionTitle,
  InputsContainer,
  PageHeader,
} from './styled';
import PageContainer from '../../../components/pageContainer';
import { SECTIONS } from '../../../constants/drawer';
import { LOAD_TITLE, convertToTitleCase } from '../../../utils/functions';
import Loader from '../../../components/loader';
import AppForm, {
  AppTextField,
  ToogleContainer,
  FormButtons,
} from '../../../components/form';
import {
  useLazyGetFileCollectionSettingsQuery,
  useUpdateFileCollectionSettingsMutation,
  useLazyGetIndividualFileCollectionQuery,
  useUpdateIngestionMutation,
} from '../../../redux/services/speciphicAsk';
import { DATA_CONFIG as API_ENDPOINT } from '../../../constants/filesCollectionSettings';
import { getChangedValues } from '../../../components/form/functions';
import UnauthorizedMessage from '../../../components/unauthorized';
import { LoadingButton } from '@mui/lab';

const translationJSONPrefix = 'filesCollectionSettingsPage';

const unauthorizedTranslationPrefix = `${translationJSONPrefix}.unauthorizedMessages`;

const FILES_COLLECTION = `${translationJSONPrefix}.filesCollection`;
const PAGE_TITLE_SUFFIX = `${translationJSONPrefix}.pageTitleSuffix`;

const PAGE_TITLE = (t) =>
  `${t(`${translationJSONPrefix}.dataConfigSettings.drawerMenuTitle`)} | ${t(
    FILES_COLLECTION,
  )} | ${t(PAGE_TITLE_SUFFIX)}`;

const defaultDataState = {
  allowFileOverwrite: false,
  ingestionIntervalDelay: '',
};

const formSchema = (t) => {
  const translationPrefix = (label) =>
    `${translationJSONPrefix}.dataConfigSettings.form.${label}.errorMessages`;

  return object({
    allowFileOverwrite: bool(),
    ingestionIntervalDelay: number().min(
      1,
      t(translationPrefix('ingestionIntervalDelay')),
    ),
  });
};

const formRenderFunction = (
  { values, errors, touched, dirty, isSubmitting, resetForm, setFieldValue },
  formSending,
) => {
  const { t } = useTranslation();

  const translationPrefix = `${translationJSONPrefix}.dataConfigSettings.form`;

  return (
    <Form>
      <InputsContainer>
        <AppTextField
          style={{ width: '50%' }}
          name="ingestionIntervalDelay"
          label={t(`${translationPrefix}.ingestionIntervalDelay.label`)}
          placeholder={t(
            `${translationPrefix}.ingestionIntervalDelay.placeholder`,
          )}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          type="number"
        />
      </InputsContainer>

      <InputsContainer>
        <ToogleContainer
          width="50%"
          name="allowFileOverwrite"
          label={t(`${translationPrefix}.allowFileOverwrite.label`)}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
        />
      </InputsContainer>

      <FormButtons
        resetForm={resetForm}
        dirty={dirty}
        isSubmitting={isSubmitting || formSending}
      />
    </Form>
  );
};

const DataConfigPage = () => {
  const params = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => LOAD_TITLE(document, PAGE_TITLE(t)), []);

  const [collectionId, setCollectionId] = useState();
  const [initialFormData, setInitialFormData] = useState(defaultDataState);
  const [formData, setFormData] = useState(defaultDataState);
  const [showAlert, setShowAlert] = useState({
    formUpdateError: false,
    formUpdateSuccess: false,
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  const [
    getFormData,
    {
      data: formDataDB,
      error: formError,
      isError: isGetFormError,
      isLoading: isGetFormLoading,
      isSuccess: isGetFormSuccess,
    },
  ] = useLazyGetFileCollectionSettingsQuery();

  const [
    updateIngestion,
    { data: updatedIngestionData, isLoading: ingestionUpdating },
  ] = useUpdateIngestionMutation();

  const [
    getFilesCollectionInfo,
    {
      data: filesCollectionInfo,
      error: filesCollectionError,
      isError: isGetFCError,
      isLoading: isGetFCLoading,
      isSuccess: isGetFCSuccess,
    },
  ] = useLazyGetIndividualFileCollectionQuery();

  useEffect(() => {
    if (params?.collectionId) {
      setCollectionId(params?.collectionId);
      getFilesCollectionInfo({ collectionId: params?.collectionId });
      getFormData({
        collectionId: params.collectionId,
        endpoint: API_ENDPOINT,
      });
    }
  }, [params?.collectionId]);

  const [
    updateSettings,
    {
      data: formDataUpdateDB,
      isError: isUpdateFormError,
      isLoading: isUpdateFormLoading,
      isSuccess: isUpdateFormSuccess,
    },
  ] = useUpdateFileCollectionSettingsMutation();

  const closeAlert = () => {
    const tempAlert = { ...showAlert };
    Object.keys(tempAlert).forEach((a) => (tempAlert[a] = false));
    setShowAlert(tempAlert);
  };

  useEffect(() => {
    const tempAlert = { ...showAlert };
    tempAlert.formUpdateError = isUpdateFormError;
    setShowAlert(tempAlert);
  }, [isUpdateFormError]);

  useEffect(() => {
    const tempAlert = { ...showAlert };
    tempAlert.formUpdateSuccess = isUpdateFormSuccess;
    setShowAlert(tempAlert);
  }, [isUpdateFormSuccess]);

  useEffect(() => {
    if (showAlert.formUpdateError || showAlert.formUpdateSuccess)
      setTimeout(closeAlert, 5000);
  }, [showAlert.formUpdateError, showAlert.formUpdateSuccess]);

  useEffect(() => {
    if (isGetFormSuccess) {
      const { allowFileOverwrite = false, ingestionIntervalDelay = '' } =
        formDataDB;

      const data = {
        allowFileOverwrite,
        ingestionIntervalDelay,
      };

      setFormData(data);
      setInitialFormData(data);
    }
  }, [formDataDB]);

  const updateIngestionEvent = () => {
    updateIngestion({
      collectionId,
      isIngestionEnabled: !formDataDB.isIngestionEnabled,
    });
  };

  if (formError?.status === 403 || filesCollectionError?.status === 403)
    return (
      <UnauthorizedMessage
        drawerOpen={isDrawerOpen}
        drawer={SECTIONS.FILES_COLLECTION_SETTINGS}
        description={t(`${unauthorizedTranslationPrefix}.list`)}
      />
    );

  return (
    <PageContainer
      isDrawerOpen={(res) => setIsDrawerOpen(res)}
      drawer={SECTIONS.FILES_COLLECTION_SETTINGS}
      collectionId={collectionId}
    >
      <PageHeader>
        {`${filesCollectionInfo?.name || '...'} / ${t(
          `${translationJSONPrefix}.dataConfigSettings.pageHeader`,
        )}`}
      </PageHeader>

      <DescriptionStyled>{collectionId}</DescriptionStyled>

      {collectionId && !isGetFormLoading ? (
        <FormContainer>
          <AlertStyled
            onClose={closeAlert}
            severity={showAlert.formUpdateError ? 'error' : 'success'}
            style={{
              marginTop:
                showAlert.formUpdateError || showAlert.formUpdateSuccess
                  ? 10
                  : -60,
            }}
          >
            {showAlert.formUpdateError &&
              t(`${translationJSONPrefix}.dataConfig.alert.updateFormError`)}
            {showAlert.formUpdateSuccess &&
              t(`${translationJSONPrefix}.dataConfig.alert.updateFormSuccess`)}
          </AlertStyled>

          <DataSourceBox>
            <Box>
              {t(`${translationJSONPrefix}.dataConfig.dataSourceLabel`)}
            </Box>
            <DataSourceType>
              <DataSourceName component="span">
                {formDataDB?.dataSourceName || '...'}
              </DataSourceName>
              {convertToTitleCase(formDataDB?.dataSourceType || '...')}
            </DataSourceType>
          </DataSourceBox>

          <IngestionContainer>
            <IngestionTitle>
              {t(`${translationJSONPrefix}.dataConfig.ingestionTitle`)}
            </IngestionTitle>

            <LoadingButton
              loading={ingestionUpdating}
              onClick={updateIngestionEvent}
              variant="contained"
            >
              {formDataDB?.isIngestionEnabled
                ? t(`${translationJSONPrefix}.dataConfig.stopIngestionButton`)
                : t(`${translationJSONPrefix}.dataConfig.startIngestionButton`)}
            </LoadingButton>
          </IngestionContainer>

          <AppForm
            initialValues={formData}
            validationSchema={formSchema(t)}
            onSubmit={(values, { setSubmitting }) => {
              const valuesToSend = getChangedValues(values, initialFormData);

              // Convert specific fields to integers
              if (valuesToSend.ingestionIntervalDelay !== undefined) {
                valuesToSend.ingestionIntervalDelay = parseInt(
                  valuesToSend.ingestionIntervalDelay,
                  10,
                );
              }

              updateSettings({
                collectionId,
                data: valuesToSend,
                endpoint: API_ENDPOINT,
              });

              setSubmitting(false);
            }}
            formRenderFunction={(formikState) =>
              formRenderFunction(formikState, isUpdateFormLoading)
            }
          />
        </FormContainer>
      ) : (
        <Loader label={t(`${translationJSONPrefix}.loadingMessage`)} />
      )}
    </PageContainer>
  );
};

export default DataConfigPage;
